import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../components/ui/Button';
import exportService from '../../services/exportService';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GaugeMeter from '../../components/ui/GaugeMeter';
import MetricCard from '../../components/ui/MetricCard';
import useWebSocket from '../../hooks/useWebSocket';
import { participantService } from '../../services';
import tokenStorage from '../../utils/tokenStorage';
import { getFeedbackRating, calculateAverageRating } from '../../utils/ratingUtils';
import {
  getMetricBandInfo,
  getARSInterpretation,
  getPCSInterpretation,
  getLIIInterpretation,
  calculateARS as calcARS,
  BY_WHEN_LABELS
} from '../../utils/reportingConstants';
import { formatLocalDate, formatActualDateTime } from '../../utils/dateUtils';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Participant Reports Page
 * Individual Participant Report - Dashboard style with engagement, challenges & action plan
 */

const ParticipantReportsPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const socket = useWebSocket(sessionId);
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showActivityStartedDialog, setShowActivityStartedDialog] = useState(false);
  const [activityInfo, setActivityInfo] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [responseFilter, setResponseFilter] = useState('all'); // Filter for All Responses tab
  const [selectedResponse, setSelectedResponse] = useState(null); // For viewing full response details

  // Fetch report data from API
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Validate sessionId before making API call
        if (!sessionId || sessionId === 'undefined' || sessionId === 'null') {
          console.error('[Reports] Invalid sessionId:', sessionId);
          setError('Invalid session. Please go back and try again.');
          setLoading(false);
          return;
        }

        console.log('[Reports] Fetching session data for:', sessionId);

        // First fetch session data to check status
        const sessionResponse = await participantService.getSession(sessionId);

        console.log('[Reports] Session response:', sessionResponse?.success, sessionResponse?.data?.name);

        if (sessionResponse.success) {
          setSession(sessionResponse.data);

          // Fetch report data if session is live, completed, or paused (for multi-day sessions)
          const sessionStatus = sessionResponse.data?.status;
          if (sessionStatus === 'live' || sessionStatus === 'completed' || sessionStatus === 'paused') {
            try {
              const reportResponse = await participantService.getSessionReport(sessionId);
              if (reportResponse.success) {
                setReportData(reportResponse.data);
              }
            } catch (reportErr) {
              // Report might not be available yet - this is okay
              console.log('Report not available yet:', reportErr.message);
            }
          }
        } else {
          setError('Failed to load session data');
        }
      } catch (err) {
        console.error('[Reports] Error fetching report data:', err);
        setError(err.message || 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [sessionId]);

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Participant data from API or localStorage
  const participant = {
    name: user.fullName || user.name || 'Participant',
    email: user.email || '',
    organization: session?.organization?.name || 'N/A',
    program: session?.name || 'Training Session',
    facilitator: session?.trainer?.name || 'Trainer',
  };

  // Overall participation snapshot from report data
  // Backend returns data in reportData.performance object
  const performance = reportData?.performance || {};
  const totalSubsessions = session?.subSessions?.length || 0;
  // Calculate total activities from session data if not provided by API
  const calculatedTotalActivities = session?.subSessions?.reduce((sum, ss) => sum + (ss.activities?.length || 0), 0) || 0;
  // Calculate completed activities from activityResponses if not provided
  const calculatedCompletedActivities = reportData?.activityResponses?.length || reportData?.responses?.length || 0;

  // Session-wise participation data - use activityResponses from API to mark completed activities
  // Backend may return as 'activityResponses' or 'responses' depending on code path
  const activityResponses = reportData?.activityResponses || reportData?.responses || [];

  // Calculate session rating from feedback responses (handles emoji ratings)
  // Check for feedback type in various formats (case-insensitive, different property names)
  const feedbackResponses = activityResponses.filter(r => {
    const type = (r.type || r.activityType || r.activity?.type || '').toLowerCase();
    return type === 'feedback' || type === 'rating' || type === 'session-feedback';
  }) || [];

  // Also check direct rating/emoji in any response
  let calculatedSessionRating = 0;
  if (feedbackResponses.length > 0) {
    calculatedSessionRating = calculateAverageRating(feedbackResponses);
  }

  // Fallback: check all responses for rating/emoji data
  if (calculatedSessionRating === 0 && activityResponses.length > 0) {
    const allRatings = [];
    activityResponses.forEach(r => {
      // Check response object for rating
      const resp = r.response;
      if (resp?.rating && typeof resp.rating === 'number') {
        allRatings.push(resp.rating);
      } else if (resp?.emoji) {
        const rating = getFeedbackRating(resp.emoji);
        if (rating > 0) allRatings.push(rating);
      } else if (r.rating && typeof r.rating === 'number') {
        allRatings.push(r.rating);
      } else if (r.emoji) {
        const rating = getFeedbackRating(r.emoji);
        if (rating > 0) allRatings.push(rating);
      }
    });
    if (allRatings.length > 0) {
      calculatedSessionRating = Math.round((allRatings.reduce((a, b) => a + b, 0) / allRatings.length) * 10) / 10;
    }
  }

  // Final fallback: check reportData.sessionRating
  if (calculatedSessionRating === 0 && reportData?.sessionRating) {
    calculatedSessionRating = getFeedbackRating(reportData.sessionRating);
  }

  // Calculate activities values for percentage calculations
  const activitiesCompletedVal = performance.activitiesCompleted || calculatedCompletedActivities;
  const totalActivitiesVal = performance.totalActivities || calculatedTotalActivities;

  const overallStats = {
    sessionsAttended: totalSubsessions,
    totalSessions: totalSubsessions,
    engagementScore: performance.engagementScore || 0,
    activitiesCompleted: activitiesCompletedVal,
    totalActivities: totalActivitiesVal,
    launchedActivities: performance.launchedActivities || performance.totalActivities || calculatedTotalActivities,
    totalActionPlans: reportData?.actionPlans?.length || session?.subSessions?.length * 2 || 0,
    totalTopics: session?.subSessions?.length || 0,
    sessionRating: calculatedSessionRating || performance.sessionRating || 0,
    averageScore: performance.averageScore || null,
    completionRate: performance.completionRate || 0,
    quizzesPassed: performance.quizzesPassed || 0,
    totalQuizzes: performance.totalQuizzes || 0,
    // Calculate percentages with fallbacks
    attendancePercent: totalSubsessions > 0 ? Math.round((totalSubsessions / totalSubsessions) * 100) : 100,
    completionPercent: totalActivitiesVal > 0 ? Math.round((activitiesCompletedVal / totalActivitiesVal) * 100) : 0,
  };

  // Build a more robust set of responded activity IDs - handle various ID formats
  // Use composite keys with subSessionId to handle same-titled activities across subsessions
  const respondedActivityIds = new Set();
  const respondedActivityTitles = new Set(); // Track by title (what backend returns)
  const respondedBySubSession = new Map(); // Map of subSessionId -> Set of (activityId or title)

  activityResponses.forEach(r => {
    // Get the subSessionId for this response
    const subSessionId = r.subSessionId?.toString() || r.subSession?._id?.toString() || '';

    // Initialize set for this subsession if not exists
    if (!respondedBySubSession.has(subSessionId)) {
      respondedBySubSession.set(subSessionId, { ids: new Set(), titles: new Set() });
    }
    const subSessionData = respondedBySubSession.get(subSessionId);

    // Add activity ID in various formats
    if (r.activityId) {
      const idStr = r.activityId.toString();
      respondedActivityIds.add(idStr);
      subSessionData.ids.add(idStr);
      // Also try removing ObjectId wrapper if present
      if (idStr.includes('ObjectId')) {
        const cleanId = idStr.replace(/ObjectId\(['"]?([^'"]+)['"]?\)/, '$1');
        respondedActivityIds.add(cleanId);
        subSessionData.ids.add(cleanId);
      }
    }
    if (r.activity?._id) {
      const idStr = r.activity._id.toString();
      respondedActivityIds.add(idStr);
      subSessionData.ids.add(idStr);
    }
    if (r.activity?.id) {
      const idStr = r.activity.id.toString();
      respondedActivityIds.add(idStr);
      subSessionData.ids.add(idStr);
    }
    // Handle activity field as string ID (MongoDB ObjectId as string)
    if (r.activity && typeof r.activity === 'string') {
      respondedActivityIds.add(r.activity);
      subSessionData.ids.add(r.activity);
    }
    // Track by title (backend returns 'title', not 'name')
    if (r.title) {
      const title = r.title.toLowerCase().trim();
      respondedActivityTitles.add(title);
      subSessionData.titles.add(title);
    }
    if (r.activityName || r.name || r.activity?.name || r.activity?.title) {
      const title = (r.activityName || r.name || r.activity?.name || r.activity?.title).toLowerCase().trim();
      respondedActivityTitles.add(title);
      subSessionData.titles.add(title);
    }
    // Also track by activityTitle field (common in backend responses)
    if (r.activityTitle) {
      const title = r.activityTitle.toLowerCase().trim();
      respondedActivityTitles.add(title);
      subSessionData.titles.add(title);
    }
  });

  // Check if ANY responses have subSessionId data - if so, use strict subsession matching
  // Only fall back to global matching for backwards compatibility with old data that has no subSessionId
  const hasSubSessionData = activityResponses.some(r => r.subSessionId || r.subSession?._id);

  // Build activity map for question lookup - map activity ID and title to activity data
  const activityMap = new Map();
  session?.subSessions?.forEach(ss => {
    ss.activities?.forEach(activity => {
      // Store by ID
      if (activity._id) {
        activityMap.set(activity._id.toString(), {
          ...activity,
          subSessionName: ss.name || ss.title,
        });
      }
      // Also store by title (lowercase) for matching when ID is not available
      if (activity.title) {
        activityMap.set(`title:${activity.title.toLowerCase().trim()}`, {
          ...activity,
          subSessionName: ss.name || ss.title,
        });
      }
    });
  });

  // Helper function to get activity question for a response
  const getActivityQuestion = (response) => {
    // Try to find by activity ID first
    const activityId = response.activityId?.toString() ||
                       response.activity?._id?.toString() ||
                       (typeof response.activity === 'string' ? response.activity : null);
    if (activityId && activityMap.has(activityId)) {
      return activityMap.get(activityId)?.question || '';
    }
    // Fallback: find by title
    const title = response.title || response.activityTitle || response.activityName || response.activity?.title || response.activity?.name;
    if (title && activityMap.has(`title:${title.toLowerCase().trim()}`)) {
      return activityMap.get(`title:${title.toLowerCase().trim()}`)?.question || '';
    }
    return '';
  };

  // Debug logging
  console.log('[ParticipantReportsPage] Activity responses:', activityResponses);
  console.log('[ParticipantReportsPage] Responded IDs:', [...respondedActivityIds]);
  console.log('[ParticipantReportsPage] Responded Titles:', [...respondedActivityTitles]);
  console.log('[ParticipantReportsPage] Responded by SubSession:', Object.fromEntries(respondedBySubSession));
  console.log('[ParticipantReportsPage] Has subSessionId data:', hasSubSessionData);
  console.log('[ParticipantReportsPage] Activity map size:', activityMap.size);

  const sessionParticipation = session?.subSessions?.map((subSession, idx) => {
    const subSessionId = subSession._id?.toString() || subSession.id?.toString() || '';
    const subSessionData = respondedBySubSession.get(subSessionId);
    const subSessionName = subSession.name || subSession.title;

    // Calculate per-subsession metrics
    let subSessionChallenges = 0;
    let subSessionActionPlans = 0;
    let subSessionARS = { value: 0, band: null };
    let subSessionPCS = { value: 0, band: null };
    let subSessionLII = { value: 0, band: null };
    const arsValues = [];

    // Get responses for this subsession
    const subSessionResponses = activityResponses.filter(r => {
      const respSubSessionId = r.subSessionId?.toString() || r.subSession?._id?.toString() || '';
      return respSubSessionId === subSessionId || r.subSessionName === subSessionName;
    });

    // Calculate metrics from subsession responses
    subSessionResponses.forEach(r => {
      const type = (r.type || r.activityType || '').toLowerCase();
      const resp = r.response || r;

      if (type === 'challenges') {
        // Count challenges
        if (resp.challenges && Array.isArray(resp.challenges)) {
          subSessionChallenges += resp.challenges.filter(c => c && c.trim()).length;
        } else if (resp.challenge || resp.text) {
          subSessionChallenges += 1;
        }
      }

      if (type === 'actionplans' || type === 'actionplan') {
        // Count action plans and calculate ARS
        if (resp.actions && Array.isArray(resp.actions)) {
          subSessionActionPlans += resp.actions.length;
          resp.actions.forEach(action => {
            if (action.byWhen && action.commitment) {
              arsValues.push(calcARS(action.byWhen, action.commitment));
            }
          });
        } else if (resp.action || resp.text) {
          subSessionActionPlans += 1;
          if (resp.byWhen && resp.commitment) {
            arsValues.push(calcARS(resp.byWhen, resp.commitment));
          }
        }
      }

      if (type === 'feedback') {
        // PCS from Q3 (confidenceToApply)
        if (resp.confidenceToApply) {
          subSessionPCS.value = resp.confidenceToApply;
          subSessionPCS.band = getMetricBandInfo('pcs', resp.confidenceToApply);
        }
        // LII from Q4 (learningAdvocacy)
        if (resp.learningAdvocacy) {
          subSessionLII.value = resp.learningAdvocacy;
          subSessionLII.band = getMetricBandInfo('lii', resp.learningAdvocacy);
        }
      }
    });

    // Calculate average ARS for subsession
    if (arsValues.length > 0) {
      subSessionARS.value = arsValues.reduce((a, b) => a + b, 0) / arsValues.length;
      subSessionARS.band = getMetricBandInfo('ars', subSessionARS.value);
    }

    return {
      day: idx + 1,
      topic: subSessionName,
      // Per-subsession metrics
      metrics: {
        activitiesCount: subSession.activities?.length || 0,
        challengesCount: subSessionChallenges,
        actionPlansCount: subSessionActionPlans,
        ars: subSessionARS,
        pcs: subSessionPCS,
        lii: subSessionLII,
      },
      activities: subSession.activities?.map(act => {
        const actId = act._id || act.id;
        const actTitle = (act.title || act.name || '').toLowerCase().trim();
        const actType = (act.type || '').toLowerCase();

        // Check subsession-specific matching (handles same-titled activities in different subsessions)
        const hasRespondedInSubSession = subSessionData && (
          (actId && subSessionData.ids.has(actId.toString())) ||
          (actTitle && subSessionData.titles.has(actTitle))
        );

        // Fallback: Check global matching (ONLY for backwards compatibility when NO responses have subSessionId)
        const hasRespondedById = actId && respondedActivityIds.has(actId.toString());
        const hasRespondedByTitle = actTitle && respondedActivityTitles.has(actTitle);
        const hasRespondedGlobally = hasRespondedById || hasRespondedByTitle;

        // IMPORTANT: If any responses have subSessionId, use STRICT subsession matching
        // Only fall back to global matching for old data that doesn't have subSessionId at all
        // This ensures "Feedback" in SubSession 1 (attended) doesn't mark "Feedback" in SubSession 2 (not attended)
        const hasResponded = hasSubSessionData ? hasRespondedInSubSession : hasRespondedGlobally;

        // Debug log for troubleshooting
        if (actType === 'feedback' || actType === 'challenges' || actType === 'actionplans') {
          console.log('[ParticipantReportsPage] Default activity check:', {
            actId: actId?.toString(),
            actTitle,
            actType,
            subSessionId,
            hasSubSessionData,
            hasRespondedInSubSession,
            hasRespondedGlobally,
            finalResult: hasResponded,
            subSessionData: subSessionData ? {
              ids: [...(subSessionData.ids || [])],
              titles: [...(subSessionData.titles || [])]
            } : null
          });
        }

        // Check if activity was launched by trainer
        const isLaunched = act.launchedAt ||
          ['active', 'launched', 'completed', 'ended', 'in_progress'].includes(act.status);

        // Determine status: completed (responded), not_attended (launched but didn't respond), not_launched (never launched)
        let actStatus = 'not_launched';
        if (hasResponded) {
          actStatus = 'completed';
        } else if (isLaunched) {
          actStatus = 'not_attended';
        }

        return {
          name: act.name || act.title,
          status: actStatus,
          detail: actType === 'qa' ? 'Q&A' :
                  actType === 'poll' ? 'Poll' :
                  actType === 'quiz' ? 'Quiz' :
                  actType === 'wordcloud' ? 'Word Cloud' :
                  actType === 'feedback' ? 'Feedback' : 'Activity',
        };
      }) || [],
      keyTakeaways: reportData?.keyTakeaways?.[idx] || [
        `Key learning from ${subSessionName}`,
      ],
    };
  }) || [];

  // Personal challenges captured topic-wise - only show actual data, no placeholders
  const personalChallenges = reportData?.challenges?.filter(c => c.challenges && c.challenges.length > 0 && c.challenges[0] !== '') || [];

  // Personal action plans auto-captured - only show actual data, no placeholders
  const personalActionPlans = reportData?.actionPlans?.filter(a => a.actions && a.actions.length > 0 && a.actions[0] !== '') || [];

  // Calculate participant performance metrics (ARS, PCS, LII)
  const calculateParticipantMetrics = () => {
    const metrics = {
      ars: { value: 0, band: null, interpretation: '' },
      pcs: { value: 0, band: null, interpretation: '' },
      lii: { value: 0, band: null, interpretation: '' },
      hasData: false
    };

    // Calculate ARS from action plan responses
    const actionPlanResponses = activityResponses.filter(r => {
      const type = (r.type || r.activityType || '').toLowerCase();
      return type === 'actionplans' || type === 'actionplan';
    });

    if (actionPlanResponses.length > 0) {
      const arsValues = [];
      actionPlanResponses.forEach(r => {
        const resp = r.response || r;
        if (resp.byWhen && resp.commitment) {
          arsValues.push(calcARS(resp.byWhen, resp.commitment));
        }
        // Handle array of action plans
        if (resp.actions && Array.isArray(resp.actions)) {
          resp.actions.forEach(action => {
            if (action.byWhen && action.commitment) {
              arsValues.push(calcARS(action.byWhen, action.commitment));
            }
          });
        }
      });
      if (arsValues.length > 0) {
        metrics.ars.value = arsValues.reduce((a, b) => a + b, 0) / arsValues.length;
        metrics.ars.band = getMetricBandInfo('ars', metrics.ars.value);
        metrics.hasData = true;

        // Get interpretation using the function (3 bands: High ≥0.70, Moderate 0.40-0.69, Low <0.40)
        metrics.ars.interpretation = getARSInterpretation(metrics.ars.value, 'participant');
      }
    }

    // Calculate PCS and LII from feedback responses
    feedbackResponses.forEach(r => {
      const resp = r.response || r;

      // PCS from Q3 (confidenceToApply) - 3 bands: High ≥4.0, Moderate 3.0-3.99, Low <3.0
      if (resp.confidenceToApply) {
        metrics.pcs.value = resp.confidenceToApply;
        metrics.pcs.band = getMetricBandInfo('pcs', metrics.pcs.value);
        metrics.hasData = true;
        metrics.pcs.interpretation = getPCSInterpretation(metrics.pcs.value, 'participant');
      }

      // LII from Q4 (learningAdvocacy) - 5 bands per PDF Page 10
      if (resp.learningAdvocacy) {
        metrics.lii.value = resp.learningAdvocacy;
        metrics.lii.band = getMetricBandInfo('lii', metrics.lii.value);
        metrics.hasData = true;
        metrics.lii.interpretation = getLIIInterpretation(metrics.lii.value, 'participant');
      }
    });

    return metrics;
  };

  const participantMetrics = calculateParticipantMetrics();

  // Report tabs configuration
  const reportTabs = [
    { id: 'overview', label: 'Overview', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    )},
    { id: 'participation', label: 'Participation', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    )},
    { id: 'challenges', label: 'Challenges', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    )},
    { id: 'actionPlans', label: 'Action Plans', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )},
    { id: 'responses', label: 'All Responses', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    )},
  ];

  // Calculate follow-up dates based on session duration
  const calculateFollowUpDates = () => {
    // Get session end date
    const sessionEndDate = session?.endTime
      || session?.actualEndTime
      || session?.scheduledEndTime
      || session?.subSessions?.slice(-1)[0]?.endedAt
      || session?.completedAt
      || new Date();

    const endDate = new Date(sessionEndDate);

    // Calculate session duration in days
    const sessionStartDate = session?.startTime
      || session?.actualStartTime
      || session?.scheduledDate
      || session?.scheduledStartTime
      || session?.subSessions?.[0]?.startedAt
      || session?.createdAt;

    let durationDays = 1;
    if (sessionStartDate) {
      const startDate = new Date(sessionStartDate);
      durationDays = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
    }

    // Consider number of subsessions as proxy for program length
    const numSubSessions = session?.subSessions?.length || 1;

    // Calculate follow-up intervals based on duration
    let firstReviewDays, secondReviewDays;

    if (durationDays <= 2 && numSubSessions <= 2) {
      // Short programs
      firstReviewDays = 7;
      secondReviewDays = 21;
    } else if (durationDays <= 5 && numSubSessions <= 4) {
      // Medium programs
      firstReviewDays = 14;
      secondReviewDays = 30;
    } else {
      // Long programs
      firstReviewDays = 21;
      secondReviewDays = 45;
    }

    const firstReviewDate = new Date(endDate);
    firstReviewDate.setDate(firstReviewDate.getDate() + firstReviewDays);

    const secondReviewDate = new Date(endDate);
    secondReviewDate.setDate(secondReviewDate.getDate() + secondReviewDays);

    return {
      firstReview: {
        days: firstReviewDays,
        date: firstReviewDate,
      },
      secondReview: {
        days: secondReviewDays,
        date: secondReviewDate,
      },
    };
  };

  const nextFollowUp = calculateFollowUpDates();

  // Calculate completion rate based on launched activities
  const completionRate = (overallStats.launchedActivities || overallStats.totalActivities) > 0
    ? Math.round((overallStats.activitiesCompleted / (overallStats.launchedActivities || overallStats.totalActivities)) * 100)
    : 0;

  // Generate PDF Report - Uses backend puppeteer-based PDF generation
  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      // Get participant ID from dual storage (auto-restores from sessionStorage if purged)
      const parsedParticipant = tokenStorage.getParticipantData(sessionId);
      let participantId = parsedParticipant ? (parsedParticipant.participantId || parsedParticipant.id || parsedParticipant._id) : null;

      if (!participantId) {
        console.error('[PDF] No participant ID found');
        throw new Error('Unable to identify participant. Please rejoin the session.');
      }

      console.log('[PDF] Exporting participant report for:', participantId);

      // Call the backend API to generate the PDF
      await exportService.exportParticipantDetailedReportPDF(
        sessionId,
        participantId,
        participant.name
      );

      console.log('[PDF] Report downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(error.message || 'Failed to generate report. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Navigation items for sidebar
  // Check if session is completed to show additional menu items
  const isSessionCompleted = session?.status === 'completed' || session?.status === 'ended';

  // Check if it's a multi-day session (different start and end dates)
  const isMultiDaySession = session?.startTime && session?.endTime &&
    new Date(session.startTime).toISOString().slice(0, 10) !== new Date(session.endTime).toISOString().slice(0, 10);

  // Show report/content items when session is completed OR when paused (for multi-day sessions)
  const showReportAndContent = isSessionCompleted || (session?.status === 'paused' && isMultiDaySession);

  // Base navigation items (always visible)
  const baseNavigationItems = [
    {
      id: 'back',
      name: '← Back',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/lobby`),
    },
    {
      id: 'welcome',
      name: 'Welcome',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/lobby`),
    },
    {
      id: 'activities',
      name: 'Aktivities',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/active`),
      badge: session?.status === 'live' ? 'Live' : null,
    },
    {
      id: 'workspace',
      name: 'My Workspace',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/notes`),
    },
  ];

  // Items only shown after session is completed
  const completedSessionItems = [
    {
      id: 'content',
      name: 'Content',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/workbooks`),
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  // Combine navigation items based on session status
  // Show content and reports when session is completed OR paused (for multi-day sessions)
  const navigationItems = showReportAndContent
    ? [...baseNavigationItems, ...completedSessionItems]
    : baseNavigationItems;

  const bottomNavItems = [
    {
      id: 'settings',
      name: 'Settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      onClick: () => {},
    },
    {
      id: 'help',
      name: 'Help Center',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  // Listen for activity launched events
  useEffect(() => {
    if (!socket || !sessionId) return;

    const handleActivityLaunched = (data) => {
      console.log('[Reports] Activity launched:', data);
      setActivityInfo(data);
      setShowActivityStartedDialog(true);
    };

    socket.on('activity:launched', handleActivityLaunched);

    return () => {
      socket.off('activity:launched', handleActivityLaunched);
    };
  }, [socket, sessionId]);

  const handleGoToActivity = () => {
    setShowActivityStartedDialog(false);
    navigate(`/session/${sessionId}/active`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3"></div>
          <p className="text-neutral-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Unable to Load Report</h2>
          <p className="text-neutral-600 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => window.location.reload()} variant="primary">
              Try Again
            </Button>
            <Button onClick={() => navigate(`/session/${sessionId}/lobby`)} variant="ghost">
              Back to Session
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Session not yet started - show friendly message
  // But allow reports when session is paused (for multi-day sessions)
  const isPausedMultiDay = session?.status === 'paused' && isMultiDaySession;
  if (session && session.status !== 'live' && session.status !== 'completed' && !isPausedMultiDay) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Report Coming Soon</h2>
          <p className="text-neutral-600 mb-2">
            Your session report will be available once the session starts.
          </p>
          <p className="text-sm text-neutral-500 mb-6">
            Session Status: <span className="capitalize font-medium text-primary-600">{session.status || 'Scheduled'}</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate(`/session/${sessionId}/lobby`)} variant="primary">
              Go to Session
            </Button>
            <Button onClick={() => navigate(-1)} variant="ghost">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No session found
  if (!session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Report not available</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex overflow-x-hidden">
      {/* Sidebar - Desktop */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img
                  src={logoFull}
                  alt="Aktivity"
                  className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                className="w-full pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-lg text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    item.id === 'reports'
                      ? 'text-white'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`}
                  style={item.id === 'reports' ? { backgroundColor: '#ff8a00' } : {}}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-neutral-100 transition-all"
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <svg
              className="w-5 h-5 transition-transform"
              style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0 bg-neutral-100 pb-20 lg:pb-0">
        {/* Top Header */}
        <header className="bg-white/95 backdrop-blur-sm border-b border-neutral-100 px-4 sm:px-6 py-3.5 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={() => navigate(`/session/${sessionId}/lobby`)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            </div>

            {/* Title */}
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">My Report</h1>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={generatePDF}
                disabled={isGeneratingPDF}
                className="flex items-center gap-2"
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="hidden sm:inline">Generating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="hidden sm:inline">Export Report</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto pb-6" ref={reportRef}>
          {/* Activity Overview Header */}
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">Activity Overview</h1>

          {/* Report Tabs */}
          <div className="mb-5">
            <div className="flex bg-neutral-100 rounded-xl p-1 gap-0.5">
              {reportTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-3 py-2 sm:py-2.5 rounded-lg text-[10px] sm:text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  <span className="[&>svg]:w-3.5 [&>svg]:h-3.5 sm:[&>svg]:w-4 sm:[&>svg]:h-4">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.id === 'actionPlans' ? 'Action' : tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Header with participant info */}
          <div className="bg-white rounded-2xl border border-neutral-100 p-4 sm:p-5 mb-5">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold text-neutral-900 truncate">{participant.name}</h2>
                <p className="text-sm text-neutral-500 mt-0.5 truncate">{participant.program}</p>
              </div>
              <div className="flex flex-col gap-2 text-sm text-neutral-600">
                <span className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 flex-shrink-0 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="truncate text-sm">{participant.email}</span>
                </span>
                <span className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 flex-shrink-0 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="truncate text-sm">{participant.organization}</span>
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-neutral-50 rounded-xl self-start">
                <span className="text-xs text-neutral-400">Facilitator:</span>
                <span className="text-sm font-semibold text-neutral-700">{participant.facilitator}</span>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
            <div className="bg-white rounded-2xl border border-neutral-100 p-3.5 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-neutral-500">Sessions</span>
                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{overallStats.sessionsAttended}/{overallStats.totalSessions}</p>
              <p className="text-xs text-neutral-400 mt-0.5">{overallStats.attendancePercent}% attendance</p>
            </div>
            <div className="bg-white rounded-2xl border border-neutral-100 p-3.5 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-neutral-500">Engagement</span>
                <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-primary-600">{overallStats.engagementScore}%</p>
              <p className="text-xs text-neutral-400 mt-0.5">Overall score</p>
            </div>
            <div className="bg-white rounded-2xl border border-neutral-100 p-3.5 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-neutral-500">Activities</span>
                <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{overallStats.activitiesCompleted}/{overallStats.totalActivities}</p>
              <p className="text-xs text-neutral-400 mt-0.5">{overallStats.completionPercent}% completed</p>
            </div>
            <div className="bg-white rounded-2xl border border-neutral-100 p-3.5 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-neutral-500">Rating Given</span>
                <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{overallStats.sessionRating > 0 ? `${overallStats.sessionRating}/5` : '-'}</p>
              <p className="text-xs text-neutral-400 mt-0.5">Session rating</p>
            </div>
          </div>

          {/* Performance Metrics Section - Only show if we have metric data */}
          {participantMetrics.hasData && activeTab === 'overview' && (
            <div className="bg-white rounded-2xl border border-neutral-100 p-4 sm:p-5 mb-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">My Performance Metrics</h3>
                  <p className="text-xs text-neutral-500">Your personalized learning indicators</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* ARS Gauge */}
                {participantMetrics.ars.value > 0 && (
                  <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                    <GaugeMeter
                      value={participantMetrics.ars.value}
                      maxValue={1}
                      label="Action Readiness"
                      band={participantMetrics.ars.band?.label || 'Pending'}
                      color={participantMetrics.ars.band?.color || 'gray'}
                      size="sm"
                    />
                    {participantMetrics.ars.interpretation && (
                      <p className="mt-3 text-xs text-neutral-600 text-center">
                        {participantMetrics.ars.interpretation}
                      </p>
                    )}
                  </div>
                )}

                {/* PCS Gauge */}
                {participantMetrics.pcs.value > 0 && (
                  <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                    <GaugeMeter
                      value={participantMetrics.pcs.value}
                      maxValue={5}
                      label="Confidence Signal"
                      band={participantMetrics.pcs.band?.label || 'Pending'}
                      color={participantMetrics.pcs.band?.color || 'gray'}
                      size="sm"
                    />
                    {participantMetrics.pcs.interpretation && (
                      <p className="mt-3 text-xs text-neutral-600 text-center">
                        {participantMetrics.pcs.interpretation}
                      </p>
                    )}
                  </div>
                )}

                {/* LII Gauge */}
                {participantMetrics.lii.value > 0 && (
                  <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                    <GaugeMeter
                      value={participantMetrics.lii.value}
                      maxValue={5}
                      label="Implementation Intent"
                      band={participantMetrics.lii.band?.label || 'Pending'}
                      color={participantMetrics.lii.band?.color || 'gray'}
                      size="sm"
                    />
                    {participantMetrics.lii.interpretation && (
                      <p className="mt-3 text-xs text-neutral-600 text-center">
                        {participantMetrics.lii.interpretation}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Session Participation */}
          {(activeTab === 'overview' || activeTab === 'participation') && (
          <div className="bg-white rounded-2xl border border-neutral-100 p-4 sm:p-5 mb-5">
            {/* Section Header */}
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-neutral-900">Session Participation</h3>
            </div>

            <div className="space-y-6">
              {sessionParticipation.map((session, idx) => (
                <div key={session.day} className={idx > 0 ? 'pt-6 border-t border-neutral-100' : ''}>
                  {/* Topic Badge */}
                  <div className="flex items-center justify-center mb-4">
                    <span className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-full">{session.topic}</span>
                  </div>

                  {/* Per-Subsession Metrics - Counts */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                    <div className="bg-neutral-50 rounded-lg p-2 sm:p-3 text-center">
                      <p className="text-base sm:text-lg font-bold text-primary-600">{session.metrics.activitiesCount}</p>
                      <p className="text-[10px] sm:text-xs text-neutral-500">Activities</p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-2 sm:p-3 text-center">
                      <p className="text-base sm:text-lg font-bold text-orange-600">{session.metrics.challengesCount}</p>
                      <p className="text-[10px] sm:text-xs text-neutral-500">Challenges</p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-2 sm:p-3 text-center">
                      <p className="text-base sm:text-lg font-bold text-green-600">{session.metrics.actionPlansCount}</p>
                      <p className="text-[10px] sm:text-xs text-neutral-500">Action Plans</p>
                    </div>
                  </div>

                  {/* Per-Subsession Metrics - Indices */}
                  {(session.metrics.ars.value > 0 || session.metrics.pcs.value > 0 || session.metrics.lii.value > 0) && (
                    <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                      {session.metrics.ars.value > 0 && (
                        <div className="bg-orange-50 rounded-lg p-2 sm:p-3 text-center">
                          <p className="text-base sm:text-lg font-bold text-orange-600">{(session.metrics.ars.value * 100).toFixed(0)}%</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Action Readiness</p>
                          {session.metrics.ars.band && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-100 text-orange-700">
                              {session.metrics.ars.band.label}
                            </span>
                          )}
                        </div>
                      )}
                      {session.metrics.pcs.value > 0 && (
                        <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                          <p className="text-base sm:text-lg font-bold text-blue-600">{session.metrics.pcs.value.toFixed(1)}/5</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Confidence</p>
                          {session.metrics.pcs.band && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">
                              {session.metrics.pcs.band.label}
                            </span>
                          )}
                        </div>
                      )}
                      {session.metrics.lii.value > 0 && (
                        <div className="bg-orange-50 rounded-lg p-2 sm:p-3 text-center">
                          <p className="text-base sm:text-lg font-bold text-orange-600">{session.metrics.lii.value.toFixed(1)}/5</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Implementation</p>
                          {session.metrics.lii.band && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-100 text-orange-700">
                              {session.metrics.lii.band.label}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Activities List */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {session.activities.map((activity, i) => {
                      const isCompleted = activity.status === 'completed';
                      const isNotAttended = activity.status === 'not_attended';

                      // Determine dot color and text
                      let dotColor = 'bg-neutral-300';
                      let textColor = 'text-neutral-500';
                      let statusText = 'Not Launched';

                      if (isCompleted) {
                        dotColor = 'bg-green-500';
                        textColor = 'text-green-600';
                        statusText = 'Completed';
                      } else if (isNotAttended) {
                        dotColor = 'bg-amber-400';
                        textColor = 'text-amber-600';
                        statusText = 'Not Attended';
                      }

                      return (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 bg-neutral-50 rounded-lg">
                          <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-neutral-900 truncate">{activity.name}</p>
                            <p className={`text-[10px] truncate ${textColor}`}>
                              {statusText}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* My Challenges Section */}
          {(activeTab === 'overview' || activeTab === 'challenges') && (
          <div className="mb-5">
            <div className="bg-white rounded-2xl border border-neutral-100">
              <div className="p-4 sm:p-5 border-b border-neutral-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">My Challenges</h3>
                    <p className="text-xs text-neutral-500">{personalChallenges.length} topics identified</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {personalChallenges.map((item, idx) => (
                    <div key={idx} className="border border-neutral-100 rounded-xl p-4">
                      <p className="text-xs font-semibold text-orange-600 mb-3">Topic: {item.topic}</p>
                      <div className="pl-4 border-l-2 border-orange-200">
                        {item.challenges.map((challenge, i) => (
                          <p key={i} className="text-sm text-neutral-700 leading-relaxed mb-2 last:mb-0">
                            "{challenge}"
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {personalChallenges.length === 0 && (
                  <p className="text-sm text-neutral-400 italic text-center py-4">No challenges reported</p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* My Action Plans Section */}
          {(activeTab === 'overview' || activeTab === 'actionPlans') && (
          <div className="mb-5">
            <div className="bg-white rounded-2xl border border-neutral-100">
              <div className="p-4 sm:p-5 border-b border-neutral-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">My Action Plans</h3>
                    <p className="text-xs text-neutral-500">{personalActionPlans.reduce((sum, a) => sum + (a.actions?.length || 0), 0)} plans across topics</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {personalActionPlans.map((item, idx) => (
                    <div key={idx} className="border border-neutral-100 rounded-xl p-4">
                      <p className="text-xs font-semibold text-orange-600 mb-3">Topic: {item.topic}</p>
                      <div className="pl-4 border-l-2 border-green-200">
                        {item.actions.map((action, i) => (
                          <p key={i} className="text-sm text-neutral-700 leading-relaxed mb-2 last:mb-0">
                            {typeof action === 'string' ? action : action.text || action.action || '-'}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {personalActionPlans.length === 0 && (
                  <p className="text-sm text-neutral-400 italic text-center py-4">No action plans created</p>
                )}

                {/* Follow-up */}
                {personalActionPlans.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    <p className="text-xs font-semibold text-neutral-700 mb-2">{participant.name.split(' ')[0]}'s Next Follow-Up:</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                        <strong>1st Review:</strong> +{nextFollowUp.firstReview.days} days ({formatLocalDate(nextFollowUp.firstReview.date)})
                      </span>
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                        <strong>2nd Review:</strong> +{nextFollowUp.secondReview.days} days ({formatLocalDate(nextFollowUp.secondReview.date)})
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}


          {/* All Responses Tab */}
          {activeTab === 'responses' && (
          <div className="bg-white rounded-2xl border border-neutral-100">
            <div className="p-4 sm:p-5 border-b border-neutral-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">All My Responses</h3>
                    <p className="text-xs text-neutral-500">{activityResponses.length} responses recorded</p>
                  </div>
                </div>
                {/* Filter dropdown */}
                <select
                  value={responseFilter}
                  onChange={(e) => setResponseFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white w-full sm:w-auto appearance-none pr-8 cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.1em 1.1em' }}
                >
                  <option value="all">All Types</option>
                  <option value="poll">Polls</option>
                  <option value="quiz">Quizzes</option>
                  <option value="wordcloud">Word Clouds</option>
                  <option value="qa">Q&A</option>
                  <option value="challenges">Challenges</option>
                  <option value="actionplans">Action Plans</option>
                  <option value="feedback">Feedback</option>
                </select>
              </div>
            </div>

            {/* Table-based Response View */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead className="bg-neutral-50/50 border-b border-neutral-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Activity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Response</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {activityResponses
                    .filter(r => {
                      if (responseFilter === 'all') return true;
                      const type = (r.type || r.activityType || r.activity?.type || '').toLowerCase();
                      return type === responseFilter || type.includes(responseFilter);
                    })
                    .map((response, idx) => {
                      const type = (response.type || response.activityType || response.activity?.type || 'activity').toLowerCase();
                      const title = response.title || response.activityTitle || response.activityName || response.activity?.title || response.activity?.name || 'Activity';
                      const topic = response.subSessionName || response.subSession?.name || response.topic || '-';
                      const timestamp = response.respondedAt || response.createdAt || response.timestamp;
                      const resp = response.response || response;

                      const typeBadgeColors = {
                        poll: 'bg-blue-100 text-blue-700',
                        quiz: 'bg-purple-100 text-purple-700',
                        wordcloud: 'bg-cyan-100 text-cyan-700',
                        qa: 'bg-amber-100 text-amber-700',
                        openquestion: 'bg-indigo-100 text-indigo-700',
                        challenges: 'bg-orange-100 text-orange-700',
                        actionplans: 'bg-green-100 text-green-700',
                        actionplan: 'bg-green-100 text-green-700',
                        feedback: 'bg-pink-100 text-pink-700',
                      };

                      const typeLabel = type === 'qa' ? 'Q&A' :
                        type === 'wordcloud' ? 'Word Cloud' :
                        type === 'actionplans' || type === 'actionplan' ? 'Action Plan' :
                        type === 'openquestion' ? 'Open Question' :
                        type.charAt(0).toUpperCase() + type.slice(1);

                      // Get response summary based on type
                      const getResponseSummary = () => {
                        if (type === 'poll') {
                          return resp.selectedLabel ||
                            (Array.isArray(resp.selectedLabels) ? resp.selectedLabels.join(', ') : null) ||
                            (typeof resp.selectedOption === 'string' ? resp.selectedOption : null) ||
                            resp.answer || resp.text || 'Vote submitted';
                        }
                        if (type === 'quiz') {
                          const correct = resp.results?.correct ?? 0;
                          const incorrect = resp.results?.incorrect ?? 0;
                          return `${correct} correct, ${incorrect} incorrect`;
                        }
                        if (type === 'wordcloud') {
                          const words = Array.isArray(resp.words) ? resp.words : (resp.word ? [resp.word] : []);
                          return words.length > 0 ? words.join(', ') : (resp.text || '-');
                        }
                        if (type === 'qa' || type === 'openquestion') {
                          // Open question stores in 'response' field, Q&A stores in 'question' or 'answer'
                          const text = resp.response || resp.question || resp.answer || resp.text || '-';
                          return text.length > 100 ? text.substring(0, 100) + '...' : text;
                        }
                        if (type === 'challenges') {
                          if (Array.isArray(resp.challenges) && resp.challenges.length > 0) {
                            return `${resp.challenges.length} challenge(s)`;
                          }
                          return resp.challenge || resp.text || '-';
                        }
                        if (type === 'actionplans' || type === 'actionplan') {
                          const actions = resp.actionPlans || resp.actions || [];
                          if (Array.isArray(actions) && actions.length > 0) {
                            return `${actions.length} action plan(s)`;
                          }
                          return resp.text || resp.plan || resp.action || '-';
                        }
                        if (type === 'feedback') {
                          if (resp.rating) return `Rating: ${resp.rating}/5`;
                          if (resp.emoji) return resp.emoji;
                          return 'Feedback submitted';
                        }
                        return typeof resp === 'string' ? resp : (resp.text || resp.value || resp.answer || 'Submitted');
                      };

                      // Get question for this response
                      const activityQuestion = getActivityQuestion(response);

                      return (
                        <tr key={idx} className="hover:bg-neutral-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-neutral-900">{title}</div>
                            {activityQuestion && (
                              <div className="text-xs text-neutral-500 mt-0.5 max-w-xs truncate" title={activityQuestion}>
                                Q: {activityQuestion}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeBadgeColors[type] || 'bg-neutral-100 text-neutral-700'}`}>
                              {typeLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-neutral-600 max-w-xs truncate">{getResponseSummary()}</td>
                          <td className="px-4 py-3 text-neutral-500 text-xs whitespace-nowrap">
                            {timestamp ? formatActualDateTime(timestamp) : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => setSelectedResponse({
                                ...response,
                                type,
                                title,
                                topic,
                                timestamp,
                                response: resp,
                                typeBadgeColors,
                                activityQuestion: getActivityQuestion(response)
                              })}
                              className="px-3 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Empty state */}
            {activityResponses.filter(r => {
              if (responseFilter === 'all') return true;
              const type = (r.type || r.activityType || r.activity?.type || '').toLowerCase();
              return type === responseFilter || type.includes(responseFilter);
            }).length === 0 && (
              <div className="text-center py-12">
                <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm text-neutral-600">No responses found</p>
                <p className="text-xs text-neutral-500 mt-1">
                  {responseFilter !== 'all' ? 'Try selecting a different filter' : 'Participate in activities to see your responses here'}
                </p>
              </div>
            )}
          </div>
          )}

          {/* Response Detail Modal */}
          <AnimatePresence>
            {selectedResponse && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedResponse(null)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${selectedResponse.typeBadgeColors?.[selectedResponse.type] || 'bg-neutral-100 text-neutral-700'}`}>
                        {selectedResponse.type === 'qa' ? 'Q&A' : selectedResponse.type === 'wordcloud' ? 'Word Cloud' : selectedResponse.type === 'actionplans' || selectedResponse.type === 'actionplan' ? 'Action Plan' : selectedResponse.type?.charAt(0).toUpperCase() + selectedResponse.type?.slice(1)}
                      </span>
                      <h3 className="text-sm font-semibold text-neutral-900">{selectedResponse.title}</h3>
                    </div>
                    <button
                      onClick={() => setSelectedResponse(null)}
                      className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-4 overflow-y-auto max-h-[60vh]">
                    {selectedResponse.topic !== '-' && (
                      <div className="mb-3">
                        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Topic</p>
                        <p className="text-sm text-neutral-700">{selectedResponse.topic}</p>
                      </div>
                    )}
                    {/* Activity Question */}
                    {selectedResponse.activityQuestion && (
                      <div className="mb-3">
                        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Question</p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm text-neutral-900">{selectedResponse.activityQuestion}</p>
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Your Response</p>
                      <div className="bg-neutral-50 rounded-lg p-3 space-y-3">
                        {/* Quiz detailed view */}
                        {selectedResponse.type === 'quiz' && (() => {
                          const resp = selectedResponse.response || selectedResponse;
                          const results = resp.results || {};
                          const answers = resp.answers || {};
                          const questionDetails = resp.questionDetails || [];
                          const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
                          return (
                            <div className="space-y-3">
                              {/* Results breakdown */}
                              {(results.correct !== undefined || results.incorrect !== undefined) && (
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="p-2 bg-green-50 rounded-lg text-center">
                                    <p className="text-xs text-green-600 font-medium">Correct</p>
                                    <p className="text-lg font-bold text-green-700">{results.correct ?? 0}</p>
                                  </div>
                                  <div className="p-2 bg-red-50 rounded-lg text-center">
                                    <p className="text-xs text-red-600 font-medium">Incorrect</p>
                                    <p className="text-lg font-bold text-red-700">{results.incorrect ?? 0}</p>
                                  </div>
                                </div>
                              )}
                              {/* Questions answered count */}
                              {Object.keys(answers).length > 0 && (
                                <p className="text-xs text-neutral-500 text-center mb-2">
                                  {Object.keys(answers).length} question{Object.keys(answers).length !== 1 ? 's' : ''} answered
                                </p>
                              )}
                              {/* Question Details */}
                              {questionDetails.length > 0 && (
                                <div className="space-y-3 pt-2 border-t border-neutral-200">
                                  <p className="text-xs text-neutral-500 uppercase tracking-wider font-medium">Question Details</p>
                                  {questionDetails.map((q, idx) => (
                                    <div key={idx} className={`p-3 rounded-lg border ${q.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                                      <div className="flex items-start gap-2 mb-2">
                                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${q.isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                                          {q.isCorrect ? '✓' : '✗'}
                                        </span>
                                        <p className="text-sm font-medium text-neutral-900">Q{q.questionNumber}. {q.question}</p>
                                      </div>
                                      <div className="ml-7 space-y-1 text-xs">
                                        <p className="text-neutral-600">
                                          <span className="font-medium">Your answer: </span>
                                          {q.userAnswer !== null && q.userAnswer !== undefined ? (
                                            <span className={q.isCorrect ? 'text-green-700' : 'text-red-700'}>
                                              {optionLetters[q.userAnswer]}. {q.options?.[q.userAnswer] || 'Unknown'}
                                            </span>
                                          ) : (
                                            <span className="text-neutral-400 italic">Not answered</span>
                                          )}
                                        </p>
                                        {!q.isCorrect && q.correctAnswer !== undefined && (
                                          <p className="text-neutral-600">
                                            <span className="font-medium">Correct answer: </span>
                                            <span className="text-green-700">
                                              {optionLetters[q.correctAnswer]}. {q.options?.[q.correctAnswer] || 'Unknown'}
                                            </span>
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* ActionPlans detailed view */}
                        {(selectedResponse.type === 'actionplans' || selectedResponse.type === 'actionplan') && (() => {
                          const resp = selectedResponse.response || selectedResponse;
                          const actions = resp.actionPlans || resp.actions || [];
                          const byWhenLabels = {
                            'not_sure': 'Not sure yet',
                            '7_days': 'Within 7 days',
                            '15_days': 'Within 15 days',
                            '30_days': 'Within 30 days',
                            '60_days': 'Within 60 days',
                            '90_days': 'Within 90 days',
                          };
                          return (
                            <div className="space-y-3">
                              {Array.isArray(actions) && actions.length > 0 ? (
                                actions.map((plan, idx) => (
                                  <div key={idx} className="p-3 bg-white rounded-lg border border-neutral-200">
                                    <p className="text-sm font-medium text-neutral-900 mb-2">
                                      {idx + 1}. {plan.text || plan.action || plan.plan || 'No plan text'}
                                    </p>
                                    <div className="flex flex-wrap gap-2 text-xs">
                                      {plan.byWhen && (
                                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                                          {byWhenLabels[plan.byWhen] || plan.byWhen}
                                        </span>
                                      )}
                                      {plan.commitment && (
                                        <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full">
                                          Commitment: {plan.commitment}/10
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-neutral-700">{resp.text || resp.plan || resp.action || '-'}</p>
                              )}
                            </div>
                          );
                        })()}

                        {/* Challenges detailed view */}
                        {selectedResponse.type === 'challenges' && (() => {
                          const resp = selectedResponse.response || selectedResponse;
                          const challenges = resp.challenges || [];
                          return (
                            <div className="space-y-2">
                              {Array.isArray(challenges) && challenges.length > 0 ? (
                                challenges.map((challenge, idx) => (
                                  <div key={idx} className="p-2 bg-white rounded-lg border border-neutral-200">
                                    <p className="text-sm text-neutral-900">{idx + 1}. {challenge}</p>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-neutral-700">{resp.challenge || '-'}</p>
                              )}
                            </div>
                          );
                        })()}

                        {/* Word Cloud detailed view */}
                        {selectedResponse.type === 'wordcloud' && (() => {
                          const resp = selectedResponse.response || selectedResponse;
                          const words = resp.words || (resp.word ? [resp.word] : []);
                          return (
                            <div className="flex flex-wrap gap-2">
                              {Array.isArray(words) && words.length > 0 ? (
                                words.map((word, idx) => (
                                  <span key={idx} className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium">
                                    {word}
                                  </span>
                                ))
                              ) : (
                                <p className="text-sm text-neutral-700">{resp.text || '-'}</p>
                              )}
                            </div>
                          );
                        })()}

                        {/* Poll detailed view */}
                        {selectedResponse.type === 'poll' && (() => {
                          const resp = selectedResponse.response || selectedResponse;
                          const answer = resp.selectedLabel ||
                            (Array.isArray(resp.selectedLabels) ? resp.selectedLabels.join(', ') : null) ||
                            (typeof resp.selectedOption === 'string' ? resp.selectedOption : null) ||
                            resp.answer || resp.text || 'Vote submitted';
                          return (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-xs text-blue-600 font-medium mb-2">Your Vote:</p>
                              <p className="text-sm text-neutral-900">{answer}</p>
                            </div>
                          );
                        })()}

                        {/* Q&A detailed view */}
                        {selectedResponse.type === 'qa' && (() => {
                          const resp = selectedResponse.response || selectedResponse;
                          const answerText = resp.response || resp.question || resp.answer || resp.text || '-';
                          return (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-xs text-amber-600 font-medium mb-2">Your Answer:</p>
                              <p className="text-sm text-neutral-900 whitespace-pre-wrap">{answerText}</p>
                            </div>
                          );
                        })()}

                        {/* Open Question detailed view */}
                        {selectedResponse.type === 'openquestion' && (() => {
                          const resp = selectedResponse.response || selectedResponse;
                          // Open question stores response in 'response' field
                          const answerText = resp.response || resp.question || resp.answer || resp.text || '-';
                          return (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-xs text-amber-600 font-medium mb-2">Your Answer:</p>
                              <p className="text-sm text-neutral-900 whitespace-pre-wrap">{answerText}</p>
                            </div>
                          );
                        })()}

                        {/* Feedback detailed view - Show all 5 questions */}
                        {selectedResponse.type === 'feedback' && (() => {
                          const resp = selectedResponse.response || selectedResponse;

                          // Define the 5 feedback questions
                          const feedbackQuestions = [
                            { key: 'topicRelevance', label: 'Q1: Topic Relevance', question: 'How relevant was this topic to your work?' },
                            { key: 'trainerEffectiveness', label: 'Q2: Trainer Effectiveness', question: 'How effective was the trainer in delivering the content?' },
                            { key: 'confidenceToApply', label: 'Q3: Confidence to Apply (PCS)', question: 'How confident are you in applying what you learned?' },
                            { key: 'learningAdvocacy', label: 'Q4: Learning Advocacy (LII)', question: 'How likely are you to recommend this learning to others?' },
                            { key: 'challengeResolution', label: 'Q5: Challenge Resolution', question: 'How well did this session address your challenges?' },
                          ];

                          // Check if we have the 5-question format
                          const has5Questions = feedbackQuestions.some(q => resp[q.key] !== undefined);

                          return (
                            <div className="space-y-3">
                              {/* Show 5 questions if available */}
                              {has5Questions && (
                                <div className="space-y-2">
                                  {feedbackQuestions.map((q, idx) => {
                                    const value = resp[q.key];
                                    if (value === undefined) return null;
                                    return (
                                      <div key={idx} className="bg-white border border-neutral-200 rounded-lg p-3">
                                        <p className="text-xs text-neutral-500 font-medium mb-1">{q.label}</p>
                                        <p className="text-xs text-neutral-600 mb-2">{q.question}</p>
                                        <div className="flex items-center gap-2">
                                          <div className="flex">
                                            {[1, 2, 3, 4, 5].map(star => (
                                              <span key={star} className={`text-sm ${star <= value ? 'text-yellow-400' : 'text-neutral-300'}`}>★</span>
                                            ))}
                                          </div>
                                          <span className="text-sm font-semibold text-neutral-700">{value}/5</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Fallback: Show simple rating if no 5-question format */}
                              {!has5Questions && resp.rating && (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-neutral-600">Rating:</span>
                                  <div className="flex">
                                    {[1, 2, 3, 4, 5].map(star => (
                                      <span key={star} className={star <= resp.rating ? 'text-yellow-400' : 'text-neutral-300'}>★</span>
                                    ))}
                                  </div>
                                  <span className="text-sm font-medium text-neutral-700">{resp.rating}/5</span>
                                </div>
                              )}

                              {/* Show emoji if present */}
                              {resp.emoji && (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-neutral-600">Emoji:</span>
                                  <span className="text-2xl">{resp.emoji}</span>
                                </div>
                              )}

                              {/* Show comment if present */}
                              {resp.comment && (
                                <div className="bg-neutral-100 rounded-lg p-3">
                                  <p className="text-xs text-neutral-500 font-medium mb-1">Comment:</p>
                                  <p className="text-sm text-neutral-700">{resp.comment}</p>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Default fallback */}
                        {!['quiz', 'actionplans', 'actionplan', 'challenges', 'wordcloud', 'poll', 'qa', 'openquestion', 'feedback'].includes(selectedResponse.type) && (
                          <p className="text-sm text-neutral-900 whitespace-pre-wrap">{selectedResponse.responseContent}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-4 border-t border-neutral-200">
                    <button
                      onClick={() => setSelectedResponse(null)}
                      className="w-full py-2 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-neutral-100 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-16 px-1">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0.5 px-1 py-1.5 flex-1 min-w-0 transition-colors ${
                item.id === 'reports' ? 'text-primary-600' : 'text-neutral-400 hover:text-neutral-600'
              }`}
            >
              <span className="relative [&>svg]:w-5 [&>svg]:h-5">
                {item.icon}
                {item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse ring-2 ring-white"></span>
                )}
              </span>
              <span className="text-[10px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'activities' ? 'Aktivity' : item.id === 'workspace' ? 'Notes' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Activity Started Dialog */}
      <ConfirmDialog
        isOpen={showActivityStartedDialog}
        onClose={() => setShowActivityStartedDialog(false)}
        onConfirm={handleGoToActivity}
        title={`${activityInfo?.activityType === 'poll' ? 'Poll' : activityInfo?.activityType === 'quiz' ? 'Quiz' : 'Aktivity'} Started!`}
        message={`${activityInfo?.activityTitle || 'An aktivity'} has been launched. Go back to aktivities to participate.`}
        confirmText="Go to Aktivity"
        cancelText="Stay Here"
        variant="info"
      />
    </div>
  );
};

export default ParticipantReportsPage;
